import Big from 'big.js';
import {BehaviorSubject, combineLatest} from 'rxjs';
import {filter, map, shareReplay} from 'rxjs/operators';
import {toString} from '@px/helper/num';
import {contract$} from './contract';
import {wsOpened$} from './ws';
import {wwData$} from './worker-data';
import PositionsData = ww.PositionsData;
import Msg = ww.Msg;

/**
 * 初始值设置为 null，用于显示 loading 状态
 */
export const positions$ = new BehaviorSubject<Position[]>(null);

export const marginLevels$ = new BehaviorSubject<MarginLevel[]>([]);

wsOpened$.subscribe(() => positions$.next([]));

wwData$
  .pipe(
    filter((o: Msg<PositionsData>) => o.type === 'positions'),
    map((msg) => msg.data.positions)
  )
  .subscribe((positions) => {
    if (positions.length < 1) {
      positions$.next([]);
      return;
    }
    positions$.next(positions);
  });

export const positionMerged$ = combineLatest([contract$, positions$]).pipe(
  filter(([_, positions]) => positions && positions.length > 0),
  map(([contract, positions]) => positions.find((o) => o.symbol === contract.symbol && o.posSide === 'Merged')),
  filter(Boolean),
  shareReplay<Position>(1)
);

wwData$
  .pipe(
    filter((o) => o.type === 'contract-margin-level'),
    map((msg) => msg.data.marginLevels)
  )
  .subscribe((levels) => marginLevels$.next(levels));

export function createInitPositions(contract: ContractPair, riskLimitCache: Map<string, RiskLimit[]>, leverageMarginCache: Map<number, LeverageMargins>, defaultTakerFeeRateEr: number): Position[] {
  if (!contract) {
    return [];
  }
  const {type, symbol, settleCurrency, ratioFactor, defaultLeverage = '-10', leverageMargin} = contract;
  const {riskLimitEv, initialMarginEr} = genRisk(type, symbol, leverageMargin, riskLimitCache, leverageMarginCache);
  const posSides = type === 'Perpetual' ? ['Merged'] : ['Long', 'Short'];
  // @ts-ignore
  return posSides.map((posSide) => initPosition(contract, type, posSide, symbol, riskLimitEv, settleCurrency, defaultLeverage, initialMarginEr, ratioFactor, defaultTakerFeeRateEr) as Position);
}

function genRisk(type, symbol, leverageMargin, riskLimitCache, leverageMarginCache) {
  if (type === 'Perpetual') {
    const data = riskLimitCache.get(symbol);
    if (data.length > 0) {
      const {riskLimitEv, initialMarginEr} = data[0];
      return {riskLimitEv, initialMarginEr};
    }
  } else {
    const leverageItem = leverageMarginCache.get(leverageMargin);
    const data = leverageItem.items;
    if (data.length > 0) {
      const {notionalValueRv, maxLeverage} = data[0];
      return {
        riskLimitEv: notionalValueRv || 0,
        initialMarginEr: Big(1).div(maxLeverage).round(2, 0).toNumber(),
      };
    }
  }
  return {riskLimitEv: 0, initialMarginEr: 0};
}

function initPosition(
  contract: ContractPair,
  type: string,
  posSide: string,
  symbol: string,
  riskLimitEv: number,
  settleCurrency: string,
  leverage: string,
  initMarginReqEr: number,
  ratioFactor: number,
  takerFeeRate: number
) {
  const realLeverage = Math.abs(Number(leverage));
  const leverageBig = Big(1).div(realLeverage);
  const buyValueToCostEr = Number(leverageBig.plus(Big(takerFeeRate).times(Big(2).minus(leverageBig))).times(ratioFactor));
  const sellValueToCostEr = Number(leverageBig.plus(Big(takerFeeRate).times(Big(2).plus(leverageBig))).times(ratioFactor));
  const leverageEr = Number(Big(realLeverage).times(ratioFactor));
  return {
    assignedPosBalanceEv: 0,
    avgEntryPrice: '0.000',
    avgEntryPriceEp: 0,
    bankruptCommEv: 0,
    bankruptPriceEp: 0,
    buyLeavesQty: 0,
    buyLeavesValue: '0.0000',
    buyLeavesValueEv: 0,
    buyValueToCostEr,
    createdAtNs: 0,
    crossSharedBalanceEv: 0,
    cumClosedPnlEv: 0,
    cumFundingFeeEv: 0,
    cumTransactFeeEv: 0,
    curTermRealisedPnlEv: 0,
    currency: settleCurrency,
    dataVer: 3,
    deleveragePercentileEr: 0,
    displayLeverageEr: leverageEr,
    displaySymbol: contract.displaySymbol,
    estimatedOrdLossEv: 0,
    execSeq: 0,
    freeCostEv: 0,
    freeQty: 0,
    initMarginReqEr,
    isCross: leverageEr <= 0,
    isIsolated: leverageEr > 0,
    lastFundingTime: 0,
    lastTermEndTime: 0,
    leverage: toString(leverageEr),
    leverageEr,
    leverageX: toString(leverageEr) + 'X',
    liquidationDistance: '--',
    liquidationPrice: '0.000',
    liquidationPriceEp: 0,
    maintMarginReqEr: 0,
    makerFeeRateEr: 0,
    margin: '0.0000',
    marginEv: 0,
    marginFiat: '0.00',
    marginRatio: '99900.00%',
    marginRatioRr: 999,
    markPrice: '--',
    markPriceEp: 0,
    maxRemovable: '0.0000',
    maxRemovableEv: 0,
    orderCostEv: 0,
    posCostEv: 0,
    posMode: type === 'PerpetualV2' ? 'Hedged' : 'OneWay',
    posSide: posSide as PosSide,
    type: type as TradeType,
    positionMarginEv: 0,
    positionStatus: 'Normal',
    realisedPnl: '0.0000',
    realisedPnlEv: 0,
    realisedPnlFiat: '0.00',
    riskLimit: toString(riskLimitEv),
    riskLimitEv,
    roiByLastPrice: '0.00%',
    roiByMarkPrice: '0.00%',
    sellLeavesQty: 0,
    sellLeavesValue: '0.0000',
    sellLeavesValueEv: 0,
    sellValueToCostEr,
    settleCurrency: settleCurrency,
    side: 'None',
    size: 0,
    sizeByCoin: '-',
    sizeByCont: '-',
    sizeByValue: '-',
    sizeStr: '0',
    symbol,
    takerFeeRateEr: 0,
    term: 1,
    transactTimeNs: 0,
    unrealisedPnlByLastPrice: '0.0000',
    unrealisedPnlByLastPriceFiat: '0.00',
    unrealisedPnlByMarkPrice: '0.0000',
    unrealisedPnlByMarkPriceFiat: '0.00',
    unrealisedPnlEv: 0,
    unrealisedPnlEvByLastPrice: 0,
    unrealisedPnlEvByMarkPrice: 0,
    updatedAtNs: 0,
    usedBalanceEv: 0,
    usedLimit: '0.0000',
    userID: 0,
    value: '0.0000',
    valueEv: 0,
    actualLeverage: '--',
    tradeLevel: 0,
    buyLeavesValueRv: '--',
    sellLeavesValueRv: '--',
    riskLimitIndexId: 0,
    totalOrdUsedBalanceEv: 0,
  };
}
